import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@akuna/ui";
import { Button } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateApplication } from "@/hooks/useApplications";
import {
  NextApplicationsList,
  type NextApplicationEntry,
} from "./NextApplicationsList";

type ApplicationItemRow = {
  id: string;
  name?: string | null;
  product_subtype?: { name?: string | null } | null;
};

type CreateApplicationFormValues = {
  name: string;
  applicationType: "vacina" | "medicamento";
  next_application_defaults: Array<{ item_id: string; interval_days: number }>;
};

interface CreateApplicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  onApplicationCreated?: (item: ApplicationItemRow) => void;
}

export function CreateApplicationDialog({
  open,
  onOpenChange,
  initialName = "",
  onApplicationCreated,
}: CreateApplicationDialogProps) {
  const { t } = useTranslation("reports");
  const createApplication = useCreateApplication();
  const createApplicationSchema = z.object({
    name: z.string().min(2, t("vaccines.createDialog.validation.nameMin")),
    applicationType: z.enum(["vacina", "medicamento"], {
      required_error: t("vaccines.createDialog.validation.applicationTypeRequired"),
    }),
  });

  const form = useForm<CreateApplicationFormValues>({
    resolver: zodResolver(createApplicationSchema),
    defaultValues: {
      name: initialName,
      applicationType: "vacina",
      next_application_defaults: [],
    },
  });

  const [nextAppEntries, setNextAppEntries] = useState<NextApplicationEntry[]>([]);

  useEffect(() => {
    if (open) {
      form.reset({
        name: initialName,
        applicationType: "vacina",
        next_application_defaults: [],
      });
      setNextAppEntries([]);
    }
  }, [open, initialName, form]);

  const onSubmit = async (data: CreateApplicationFormValues) => {
    try {
      const next_application_defaults = nextAppEntries
        .filter((e) => e.item_id && e.interval_days && e.interval_days > 0)
        .map((e) => ({ item_id: e.item_id!, interval_days: e.interval_days! }));

      const item = await createApplication.mutateAsync({
        name: data.name,
        applicationType: data.applicationType,
        next_application_defaults,
      });

      if (item && onApplicationCreated) {
        onApplicationCreated(item);
      }

      onOpenChange(false);
      form.reset();
      setNextAppEntries([]);
    } catch (error) {
      console.error(t("vaccines.createDialog.errors.createFailed"), error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("vaccines.cardTitle")}</DialogTitle>
          <DialogDescription>
            {t("vaccines.pageSubtitle")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("vaccines.createDialog.nameLabel")} *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("vaccines.createDialog.namePlaceholder")}
                      {...field}
                      disabled={createApplication.isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="applicationType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("vaccines.filters.subtype")} *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("vaccines.filters.subtype")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="vacina">{t("vaccines.filters.vaccineType")}</SelectItem>
                      <SelectItem value="medicamento">{t("vaccines.filters.medicationType")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <NextApplicationsList
              mode="catalog"
              entries={nextAppEntries}
              onChange={setNextAppEntries}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createApplication.isPending}
              >
                {t("cancel", { ns: "common" })}
              </Button>
              <Button type="submit" disabled={createApplication.isPending}>
                {createApplication.isPending ? t("loading", { ns: "common" }) : t("create", { ns: "common" })}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export const CreateVaccineDialog = CreateApplicationDialog;
